import React, { useContext, useState, useEffect, Fragment, useCallback, useMemo } from 'react';
import Layout from '../../components/layout/main';
import { makeStyles } from 'tss-react/mui';
import { Fade, Grid, Paper } from '@mui/material';
import * as yup from 'yup';
import { postData, getData, putData } from '../../services/ajax';
import { AuthContext } from '../../context/auth';
import { LoadingModalContext } from '../../components/modal/loading';
import { AlertModalContext } from '../../components/modal/alert';
import { setYupLocaleES } from '../../const/yup';
import { useNavigate } from 'react-router-dom';
import { CustomForm } from '../../components/formSections';
import { GlobalsContext } from '../../context/globals';
import { useSnackbar } from 'notistack';

setYupLocaleES();

const useStyles = makeStyles()((theme) => ({
    paper: {
        padding: theme.spacing(4),
        paddingBottom: theme.spacing(2),
        display: 'flex',
        overflow: 'auto',
        flexDirection: 'column',
        width: '100%',
        marginBottom: theme.spacing(2),
    },
    textWithIcon: {
        display: 'flex',
        alignItems: "center",
    },
    icon: {
        marginRight: theme.spacing(1),
    }
}));

function Page() {
    const { classes } = useStyles();
    const { usuario } = useContext(AuthContext);
    const navigate = useNavigate();

    const globalsContext = useContext(GlobalsContext);
    const [data, setData] = useState(false);

    //Modals
    const loadingModal = useContext(LoadingModalContext);
    const alertModal = useContext(AlertModalContext);
    const { enqueueSnackbar } = useSnackbar();

    const onSubmit = async (values, { resetForm }) => {
        loadingModal.setOpen(true);

        try {
            // Preparar datos para la API
            // Convertir valores numéricos a números para evitar problemas de validación
            const rawCuil = values.cuil != null && values.cuil !== "" ? String(values.cuil).replace(/\D/g, "") : "";
            const cuilParsed = rawCuil.length === 11 ? parseInt(rawCuil, 10) : null;

            const usuarioData = {
                email: values.email,
                cuil: cuilParsed,
                nombre_apellido: values.nombre_apellido,
                nivel_usuario: Number(values.nivel_usuario),
                sup_usuarios: Boolean(values.sup_usuarios),
            };

            let response;
            if (data && data.id_usuario) {
                // Editar usuario existente
                response = await putData(`usuarios/${data.id_usuario}`, usuarioData, usuario.token);
            } else {
                // Crear nuevo usuario
                response = await postData('usuarios', usuarioData, usuario.token);
            }

            if (response.error === "") {
                enqueueSnackbar(
                    data ? "Usuario modificado correctamente" : 'Usuario creado correctamente. Se ha enviado un email con el enlace para crear la contraseña.',
                    { variant: "success" }
                );
                onClose();
            } else {
                enqueueSnackbar(response.error, { variant: "error" });
            }
        } catch (error) {
            enqueueSnackbar("Error al guardar el usuario", { variant: "error" });
        } finally {
            loadingModal.setOpen(false);
        }
    };

    const onClose = useCallback(async () => {
        navigate(-1);
    }, [navigate]);

    const onCancel = () => {
        alertModal.showModal(
            "Cancelar",
            "Se perderán todos los cambios. ¿Deseas continuar?",
            function () {
                onClose();
            });
    };

    const loadData = useCallback(async () => {
        // Si tengo un id a editar, leo los datos desde el servidor
        if (globalsContext.idEdit) {
            loadingModal.setOpen(true);

            try {
                const response = await getData(`usuarios/${globalsContext.idEdit}`, usuario.token);

                if (response.error === "") {
                    // Mapear datos de la API al formato del formulario
                    const mappedData = {
                        ...response.data,
                        nombre: response.data.nombre_apellido,
                        cuitCuil: response.data.cuil != null ? String(response.data.cuil).replace(/\D/g, "") : "",
                    };
                    setData(mappedData);
                } else {
                    enqueueSnackbar(response.error, { variant: "error" });
                }
            } catch (error) {
                enqueueSnackbar("Error al cargar los datos del usuario", { variant: "error" });
            } finally {
                loadingModal.setOpen(false);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [globalsContext.idEdit, usuario.token]);

    useEffect(() => {
        if (globalsContext.idEdit) {
            loadData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [globalsContext.idEdit]);

    const formSections = useMemo(() => [
        {
            name: "Datos personales",
            fields: [
                {
                    dataName: "email", label: "Email", controlType: "textField",
                    initialValue: data ? data.email : '',
                    validationSchema: yup.string().email("Debe ser un email válido").required("El email es requerido"),
                    autoFocus: true,
                },
                {
                    dataName: "cuil",
                    label: "CUIT / CUIL (11 números, solo cifras)",
                    controlType: "textField",
                    numericCuit: true,
                    initialValue: data && data.cuitCuil != null && data.cuitCuil !== "" ? String(data.cuitCuil).replace(/\D/g, "") : "",
                    placeholder: "Ej. 20123456786",
                    helperText: "Solo números, 11 cifras, sin espacios, puntos ni guiones. Dejá vacío si aún no tenés CUIL.",
                    validationSchema: yup
                        .string()
                        .test(
                            "cuit-tam",
                            "Usá 11 números seguidos, sin espacios, puntos ni guiones (o dejá el campo vacío).",
                            (v) => {
                                const d = (v == null || v === undefined ? "" : String(v)).replace(/\D/g, "");
                                return d.length === 0 || d.length === 11;
                            }
                        )
                        .test(
                            "cuit-rango",
                            "CUIT/CUIL inválido. Revisá que las 11 cifras sean correctas.",
                            (v) => {
                                const d = (v == null || v === undefined ? "" : String(v)).replace(/\D/g, "");
                                if (d.length === 0) {
                                    return true;
                                }
                                if (d.length < 11) {
                                    return true;
                                }
                                const n = parseInt(d, 10);
                                return n >= 20000000000 && n <= 31000000000;
                            }
                        ),
                },
                {
                    dataName: "nombre_apellido", label: "Nombre y apellido", controlType: "textField",
                    initialValue: data ? data.nombre : '',
                    validationSchema: yup.string().required("El nombre es requerido"),
                },
            ],
        },
        {
            name: "Permisos y nivel",
            fields: [
                {
                    dataName: "nivel_usuario", label: "Nivel de usuario", controlType: "select",
                    items: [
                        { id: 1, nombre: "Administrador" },
                        { id: 2, nombre: "Supervisor" },
                        { id: 3, nombre: "Usuario" },
                    ],
                    initialValue: data ? data.nivel_usuario : 3,
                    validationSchema: yup.number().required(),
                },
                {
                    dataName: "sup_usuarios", label: "Supervisión de Usuarios", controlType: "checkBox",
                    initialValue: data ? data.sup_usuarios : false,
                    validationSchema: yup.boolean(),
                },
            ],
        },
    ], [data]);

    return (
        <Fragment>
            <Layout nombrePagina={data ? "Modificación de Usuario" : "Nuevo Usuario"}>
                <Fade in={true} timeout={900}>
                    <Grid size={12}>
                        <Paper className={classes.paper}>
                            <CustomForm
                                formSections={formSections}
                                onSubmit={onSubmit}
                                onCancel={onCancel}
                                submitButtonText={data ? "Guardar cambios" : "Crear usuario"}
                                cancelButtonText="Cancelar"
                                stepperType={"vertical"}
                                isModal={false}
                            />
                        </Paper>
                    </Grid>
                </Fade>
            </Layout>
        </Fragment>
    );
}

export default Page;
